// ===================================================================
// WORKFLOWS — Template Builder
// User-friendly template management with presets and inline editing.
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea,
  Select, ActionIcon, Modal, Stack, SimpleGrid, Switch, Card,
  Divider, ThemeIcon, Tooltip,
} from '@mantine/core';
import {
  IconPlus, IconEdit, IconTrash, IconCopy, IconArrowUp, IconArrowDown,
  IconSearch, IconTemplate, IconPlayerPlay, IconSparkles, IconBolt,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useWorkflowStore } from '../../store/workflowStore';
import { useAuthStore } from '../../store/authStore';
import { useRoleStore } from '../../store/roleStore';
import type {
  WorkflowTemplate, WorkflowStepDef, WorkflowStepType, AssigneeType,
  WorkflowCategory, FriendlyTrigger, StepCondition, ConditionOperator,
} from '../../types/workflow';
import {
  STEP_TYPE_LABELS, STEP_TYPE_COLORS, FRIENDLY_TRIGGERS, PRESET_WORKFLOWS,
  FRIENDLY_TRIGGER_CONFIG, EDITOR_STEP_TYPES, EDITOR_ASSIGNEE_TYPES,
  CONDITION_OPERATOR_LABELS, TRIGGER_FIELD_REGISTRY,
} from '../../types/workflow';
import type { FormSchema } from '../../types/forms';
import { useFormsStore } from '../../store/formsStore';
import {
  WORKFLOW_MODULE_OPTIONS, getTablesForModule, getTableByKey,
  getFieldNamesForTable, fieldLabel,
} from '../../lib/workflowTables';

// ── Constants ────────────────────────────────────────────────

const MODULE_OPTIONS = [
  { value: '*', label: 'All Modules' },
  { value: 'pagrants', label: 'PA Grants' },
  { value: 'impact', label: 'Impact Assessment' },
  { value: 'debris', label: 'Debris ADMS' },
  { value: 'intake', label: 'Intake' },
  { value: 'shelter', label: 'Shelter' },
  { value: 'mainstreet', label: 'MainStreet' },
  { value: 'hotwash', label: 'HotWash' },
  { value: 'handoff', label: 'Handoff' },
  { value: 'volunteer', label: 'Volunteer' },
  { value: 'forms', label: 'Forms' },
  { value: 'incidents', label: 'Incidents' },
];

const CATEGORY_OPTIONS: { value: WorkflowCategory; label: string }[] = [
  { value: 'approval', label: 'Approval' },
  { value: 'review', label: 'Review' },
  { value: 'routing', label: 'Routing' },
  { value: 'notification', label: 'Notification' },
  { value: 'custom', label: 'Custom' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const STEP_TYPE_OPTIONS = EDITOR_STEP_TYPES.map(k => ({
  value: k, label: STEP_TYPE_LABELS[k],
}));

const ASSIGNEE_TYPE_OPTIONS = EDITOR_ASSIGNEE_TYPES;

const TRIGGER_OPTIONS = FRIENDLY_TRIGGERS.map(t => ({
  value: t.value,
  label: `${t.label}${t.module ? ` (${t.module})` : ''}`,
}));

const CATEGORY_COLORS: Record<string, string> = {
  approval: 'violet', review: 'cyan', routing: 'teal',
  notification: 'indigo', custom: 'gray',
};

const PRIORITY_COLORS_MANTINE: Record<string, string> = {
  low: 'gray', normal: 'blue', high: 'orange', critical: 'red',
};

// ── Helpers ──────────────────────────────────────────────────

function makeStep(index: number): WorkflowStepDef {
  return {
    name: `Step ${index + 1}`, type: 'approval', assigneeType: 'user',
    assignee: '', notifyChannels: [], config: {}, instructions: '',
  };
}

function moduleLabel(v: string) {
  return MODULE_OPTIONS.find(o => o.value === v)?.label ?? v;
}

function triggerLabel(v: string) {
  return FRIENDLY_TRIGGERS.find(t => t.value === v)?.label ?? v;
}

function assigneeLabel(type: AssigneeType, assignee: string) {
  const opt = ASSIGNEE_TYPE_OPTIONS.find(o => o.value === type);
  if (type === 'creator' || type === 'previous_assignee') return opt?.label ?? type;
  return assignee ? `${opt?.label}: ${assignee}` : (opt?.label ?? type);
}

// ── Form state interface ─────────────────────────────────────

interface TemplateForm {
  name: string;
  description: string;
  module: string;
  category: string;
  priority: string;
  active: boolean;
  trigger: string;
  // Generic table-event trigger fields (used when trigger starts with record_event_)
  triggerModule: string;       // module name for filtering the table dropdown
  triggerTable: string;        // Ignatius table key
  triggerField: string;        // optional field filter
  triggerValue: string;        // optional value filter
}

const EMPTY_FORM: TemplateForm = {
  name: '', description: '', module: '*', category: 'approval',
  priority: 'normal', active: true, trigger: 'manual',
  triggerModule: '', triggerTable: '', triggerField: '', triggerValue: '',
};

// ── Step Editor Row ──────────────────────────────────────────

function StepRow({ step, index, total, onChange, onMoveUp, onMoveDown, onDelete, roleOptions, userOptions, formFieldOptions, allStepNames, formFieldMeta }: {
  step: WorkflowStepDef; index: number; total: number;
  onChange: (s: WorkflowStepDef) => void;
  onMoveUp: () => void; onMoveDown: () => void; onDelete: () => void;
  roleOptions: { value: string; label: string }[];
  userOptions: { value: string; label: string }[];
  formFieldOptions: { value: string; label: string }[];
  allStepNames: string[];
  formFieldMeta: Record<string, { type: string; options: { value: string; label: string }[] }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const set = (p: Partial<WorkflowStepDef>) => onChange({ ...step, ...p });
  const color = STEP_TYPE_COLORS[step.type] ?? '#64748B';

  const onRejectOptions = [
    { value: 'cancel', label: 'Cancel Workflow' },
    { value: 'end', label: 'End Workflow' },
    ...Array.from({ length: total }, (_, i) => ({ value: `goto:${i}`, label: `Go back to Step ${i + 1}` })),
  ];

  // needsAssigneeInput removed — each type now has its own Select dropdown

  return (
    <Paper withBorder p="xs" radius="sm" style={{ borderLeft: `3px solid ${color}` }}>
      <Group gap="xs" wrap="nowrap">
        <Text size="xs" c="dimmed" fw={700} w={20} ta="center">{index + 1}</Text>
        <Badge size="xs" color={color} variant="light">{STEP_TYPE_LABELS[step.type]}</Badge>
        <Text size="sm" fw={500} style={{ flex: 1, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
          {step.name || `Step ${index + 1}`}
        </Text>
        <Text size="xs" c="dimmed">{assigneeLabel(step.assigneeType, step.assignee)}</Text>
        <Group gap={2}>
          <ActionIcon size="xs" variant="subtle" disabled={index === 0} onClick={onMoveUp}><IconArrowUp size={12} /></ActionIcon>
          <ActionIcon size="xs" variant="subtle" disabled={index === total - 1} onClick={onMoveDown}><IconArrowDown size={12} /></ActionIcon>
          <ActionIcon size="xs" variant="subtle" color="red" onClick={onDelete}><IconTrash size={12} /></ActionIcon>
        </Group>
      </Group>

      {expanded && (
        <Stack gap="xs" mt="xs" pl={28}>
          <Group grow gap="xs">
            <TextInput label="Step Name" size="xs" value={step.name}
              onChange={e => set({ name: e.currentTarget.value })} />
            <Select label="Type" size="xs" data={STEP_TYPE_OPTIONS} value={step.type}
              onChange={v => {
                const t = (v as WorkflowStepType) ?? 'approval';
                const updates: Partial<WorkflowStepDef> = { type: t };
                if (t === 'notification') updates.notifyChannels = ['email'];
                if (t === 'conditional') { updates.assigneeType = 'user'; updates.assignee = 'system'; }
                set(updates);
              }} />
          </Group>

          {step.type !== 'conditional' && <Group grow gap="xs">
            <Select label="Assign To" size="xs" data={ASSIGNEE_TYPE_OPTIONS} value={step.assigneeType}
              onChange={v => set({ assigneeType: (v as AssigneeType) ?? 'user' })} />
            {step.assigneeType === 'role' && (
              <Select label="Role" size="xs" data={roleOptions} value={step.assignee}
                onChange={v => set({ assignee: v ?? '' })} searchable clearable placeholder="Select role..." />
            )}
            {step.assigneeType === 'user' && (
              <Select label="User" size="xs" data={userOptions} value={step.assignee}
                onChange={v => set({ assignee: v ?? '' })} searchable clearable placeholder="Select user..." />
            )}
          </Group>}

          {step.type !== 'conditional' && (
            <Textarea label="Instructions" size="xs" autosize minRows={2} value={step.instructions ?? ''}
              onChange={e => set({ instructions: e.currentTarget.value })}
              placeholder="Guidance shown to the assignee when this step is active" />
          )}

          {['approval', 'review'].includes(step.type) && (
            <Select label="On Reject" size="xs" data={onRejectOptions}
              value={step.onReject ?? 'cancel'}
              onChange={v => set({ onReject: v ?? 'cancel' })} />
          )}

          {step.type === 'notification' && (
            <Textarea label="Notification Message" size="xs" autosize minRows={2}
              value={step.config?.message ?? step.config?.messageTemplate ?? ''}
              onChange={e => set({ config: { ...step.config, message: e.currentTarget.value } })}
              placeholder="Message to send" />
          )}

          {step.type === 'conditional' && (() => {
            // Condition is stored directly as an object in config (NOT as a nested JSON string)
            const condition: StepCondition = (() => {
              const c = step.config?.condition;
              if (!c) return { field: '', operator: 'equals' as ConditionOperator, value: '' };
              if (typeof c === 'object') return c as unknown as StepCondition;
              try { return JSON.parse(c); } catch { return { field: '', operator: 'equals' as ConditionOperator, value: '' }; }
            })();
            const updateCond = (patch: Partial<StepCondition>) => {
              const updated = { ...condition, ...patch };
              set({ config: { ...step.config, condition: updated } });
            };
            const operatorOpts = Object.entries(CONDITION_OPERATOR_LABELS).map(([v, l]) => ({ value: v, label: l }));
            const noValueOps = ['is_empty', 'is_not_empty'];
            const stepTargetOpts = [
              { value: 'terminate', label: 'Terminate workflow', disabled: false },
              ...Array.from({ length: total }, (_, i) => ({
                value: String(i), label: `Step ${i + 1}: ${allStepNames[i] || ''}`,
                disabled: i === index,
              })),
            ];
            const selectedFieldMeta = condition.field ? formFieldMeta[condition.field] : undefined;
            const hasFieldOptions = selectedFieldMeta && selectedFieldMeta.options.length > 0;

            return (
              <Paper p="xs" radius="sm" withBorder style={{ backgroundColor: '#F8FAFC', borderColor: '#64748B44' }}>
                <Text size="xs" fw={600} mb="xs" c="dimmed">Condition: If...</Text>
                <Stack gap="xs">
                  <Group grow gap="xs">
                    {formFieldOptions.length > 0 ? (
                      <Select label="Field" size="xs" data={formFieldOptions} value={condition.field}
                        onChange={v => updateCond({ field: v ?? '', fieldLabel: formFieldOptions.find(o => o.value === v)?.label })}
                        searchable placeholder="Select form field..." />
                    ) : (
                      <TextInput label="Field" size="xs" value={condition.field}
                        onChange={e => updateCond({ field: e.currentTarget.value })}
                        placeholder="Field key name" />
                    )}
                    <Select label="Operator" size="xs" data={operatorOpts} value={condition.operator}
                      onChange={v => updateCond({ operator: (v as ConditionOperator) ?? 'equals' })} />
                  </Group>
                  {!noValueOps.includes(condition.operator) && (
                    hasFieldOptions ? (
                      <Select label="Value" size="xs" data={selectedFieldMeta!.options}
                        value={condition.value ?? ''} onChange={v => updateCond({ value: v ?? '' })}
                        searchable placeholder="Select value..." />
                    ) : (
                      <TextInput label="Value" size="xs" value={condition.value ?? ''}
                        onChange={e => updateCond({ value: e.currentTarget.value })}
                        placeholder="Comparison value" />
                    )
                  )}
                  <Group grow gap="xs">
                    <Select label="If TRUE → go to" size="xs" data={stepTargetOpts}
                      value={step.config?.conditionTrueStep != null ? (step.config.conditionTrueStep === -1 ? 'terminate' : String(step.config.conditionTrueStep)) : null}
                      onChange={v => set({ config: { ...step.config, condition: step.config?.condition, conditionTrueStep: v === 'terminate' ? -1 : v != null ? Number(v) : undefined } })}
                      placeholder="Select step..." />
                    <Select label="If FALSE → go to" size="xs" data={stepTargetOpts}
                      value={step.config?.conditionFalseStep != null ? (step.config.conditionFalseStep === -1 ? 'terminate' : String(step.config.conditionFalseStep)) : null}
                      onChange={v => set({ config: { ...step.config, condition: step.config?.condition, conditionFalseStep: v === 'terminate' ? -1 : v != null ? Number(v) : undefined } })}
                      placeholder="Select step..." />
                  </Group>
                </Stack>
              </Paper>
            );
          })()}
        </Stack>
      )}
    </Paper>
  );
}

// ── Main Component ───────────────────────────────────────────

export default function WorkflowTemplates() {
  const { templates, loading, loadTemplates, createTemplate, updateTemplate, deleteTemplate, startWorkflow } = useWorkflowStore();
  const { user } = useAuthStore();
  const { roles, loadRoles } = useRoleStore();
  const [igUsers, setIgUsers] = useState<{ value: string; label: string }[]>([]);

  // Build dropdown options for roles, users
  const roleOpts = useMemo(() => roles.map(r => ({ value: r.roleSlug || r.roleName, label: r.roleName })), [roles]);
  const userOpts = igUsers;

  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowTemplate | null>(null);
  const [editTarget, setEditTarget] = useState<WorkflowTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<TemplateForm>({ ...EMPTY_FORM });
  const [steps, setSteps] = useState<WorkflowStepDef[]>([]);
  const [triggerFormId, setTriggerFormId] = useState<string>('');

  // Forms store for trigger config
  const { templates: formTemplates, loadTemplates: loadFormTemplates } = useFormsStore();
  const formTemplateOpts = useMemo(
    () => formTemplates.filter(f => f.status === 'published').map(f => ({
      value: String(f.Rid), label: f.templateName,
    })),
    [formTemplates],
  );

  // Field options for conditional step builder — from form schema OR trigger field registry
  const formFieldOptions = useMemo(() => {
    // Form-triggered: parse schema for field labels
    if (form.trigger === 'new_form_response' && triggerFormId) {
      const ft = formTemplates.find(f => String(f.Rid) === triggerFormId || String(f.id) === triggerFormId);
      if (ft?.schema && ft.schema !== '{}') {
        try {
          const schema: FormSchema = JSON.parse(ft.schema);
          return schema.pages.flatMap(page =>
            (page.fields || [])
              .filter(f => !['section', 'instructions', 'page_break'].includes(f.type))
              .map(f => ({ value: f.key, label: f.label || f.key }))
          );
        } catch { /* fall through */ }
      }
    }
    // record_event_* triggers: derive fields from the chosen table's FIELDS schema
    if (form.trigger?.startsWith('record_event_') && form.triggerTable) {
      const names = getFieldNamesForTable(form.triggerTable);
      return names.map(n => ({ value: n, label: fieldLabel(n) }));
    }
    // All other triggers: use the static field registry
    const registry = TRIGGER_FIELD_REGISTRY[form.trigger as keyof typeof TRIGGER_FIELD_REGISTRY];
    if (registry) return registry.map(f => ({ value: f.key, label: f.label }));
    return [];
  }, [form.trigger, form.triggerTable, triggerFormId, formTemplates]);

  // Field metadata (type + options) for conditional value dropdowns
  const formFieldMeta = useMemo<Record<string, { type: string; options: { value: string; label: string }[] }>>(() => {
    // Form-triggered: parse schema for field types and options
    if (form.trigger === 'new_form_response' && triggerFormId) {
      const ft = formTemplates.find(f => String(f.Rid) === triggerFormId || String(f.id) === triggerFormId);
      if (ft?.schema && ft.schema !== '{}') {
        try {
          const schema: FormSchema = JSON.parse(ft.schema);
          const meta: Record<string, { type: string; options: { value: string; label: string }[] }> = {};
          for (const page of schema.pages || []) {
            for (const f of page.fields || []) {
              meta[f.key] = {
                type: f.type,
                options: (f.options || []).map(o => ({ value: o.value, label: o.label || o.value })),
              };
            }
          }
          return meta;
        } catch { /* fall through */ }
      }
    }
    // record_event_* triggers: every field exists, all treated as text (we
    // don't know option lists for arbitrary tables — user types the value)
    if (form.trigger?.startsWith('record_event_') && form.triggerTable) {
      const names = getFieldNamesForTable(form.triggerTable);
      const meta: Record<string, { type: string; options: { value: string; label: string }[] }> = {};
      for (const n of names) meta[n] = { type: 'text', options: [] };
      return meta;
    }
    // All other triggers: use the static field registry
    const registry = TRIGGER_FIELD_REGISTRY[form.trigger as keyof typeof TRIGGER_FIELD_REGISTRY];
    if (registry) {
      const meta: Record<string, { type: string; options: { value: string; label: string }[] }> = {};
      for (const f of registry) {
        meta[f.key] = { type: 'text', options: f.options || [] };
      }
      return meta;
    }
    return {};
  }, [form.trigger, form.triggerTable, triggerFormId, formTemplates]);

  useEffect(() => {
    loadTemplates(); loadRoles(); loadFormTemplates();
    // Load Ignatius users for the "Specific User" dropdown
    import('../../lib/api-switch').then(({ fetchIgnatiusUsers }) => {
      fetchIgnatiusUsers().then(users => {
        setIgUsers(users.map((u: any) => ({ value: u.email, label: `${u.fullName} (${u.email})` })));
      }).catch(() => {});
    });
  }, [loadTemplates, loadRoles, loadFormTemplates]);

  const setField = <K extends keyof TemplateForm>(key: K, val: TemplateForm[K]) =>
    setForm(prev => ({ ...prev, [key]: val }));

  // ── Filter + sort ─────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = [...templates];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t => t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
    }
    if (filterCategory) list = list.filter(t => t.category === filterCategory);
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [templates, search, filterCategory]);

  // ── Open editor ───────────────────────────────────────────

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM });
    setSteps([makeStep(0)]);
    setEditorOpen(true);
  };

  const openFromPreset = (preset: typeof PRESET_WORKFLOWS[number]) => {
    setEditTarget(null);
    setForm({
      name: preset.name,
      description: preset.description,
      module: preset.module,
      category: preset.category,
      priority: 'normal',
      active: true,
      trigger: preset.trigger,
      triggerModule: '', triggerTable: '', triggerField: '', triggerValue: '',
    });
    setSteps([...preset.steps]);
    setPresetOpen(false);
    setEditorOpen(true);
  };

  const openEdit = (t: WorkflowTemplate) => {
    setEditTarget(t);
    let parsed: WorkflowStepDef[] = [];
    try { parsed = JSON.parse(t.stepsJson); } catch { /* empty */ }
    setSteps(parsed.length > 0 ? parsed : [makeStep(0)]);
    const triggerMatch = FRIENDLY_TRIGGERS.find(ft => ft.value === t.triggerType);
    const isRecordEvent = t.triggerType?.startsWith('record_event_');
    setForm({
      name: t.name,
      description: t.description,
      module: t.module || '*',
      category: t.category || 'approval',
      priority: t.priority || 'normal',
      active: t.active !== 'no',
      trigger: triggerMatch ? t.triggerType : 'manual',
      triggerModule: isRecordEvent && t.triggerTable ? (getTableByKey(t.triggerTable)?.module || '') : '',
      triggerTable: isRecordEvent ? (t.triggerTable || '') : '',
      triggerField: isRecordEvent ? (t.triggerField || '') : '',
      triggerValue: isRecordEvent ? (t.triggerValue || '') : '',
    });
    // Init trigger form picker from saved triggerTable
    setTriggerFormId(t.triggerType === 'new_form_response' ? t.triggerTable : '');
    setEditorOpen(true);
  };

  const openDuplicate = (t: WorkflowTemplate) => {
    setEditTarget(null);
    let parsed: WorkflowStepDef[] = [];
    try { parsed = JSON.parse(t.stepsJson); } catch { /* empty */ }
    setSteps(parsed.length > 0 ? parsed : [makeStep(0)]);
    setForm({
      name: `${t.name} (Copy)`,
      description: t.description,
      module: t.module || '*',
      category: t.category || 'approval',
      priority: t.priority || 'normal',
      active: false,
      trigger: 'manual',
      triggerModule: '', triggerTable: '', triggerField: '', triggerValue: '',
    });
    setEditorOpen(true);
  };

  // ── Step management ───────────────────────────────────────

  const addStep = () => setSteps(prev => [...prev, makeStep(prev.length)]);
  const updateStep = (i: number, s: WorkflowStepDef) => setSteps(prev => prev.map((x, j) => j === i ? s : x));
  const moveUp = (i: number) => { if (i === 0) return; setSteps(prev => { const n = [...prev]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; }); };
  const moveDown = (i: number) => { setSteps(prev => { if (i >= prev.length - 1) return prev; const n = [...prev]; [n[i], n[i + 1]] = [n[i + 1], n[i]]; return n; }); };
  const removeStep = (i: number) => setSteps(prev => prev.filter((_, j) => j !== i));

  // ── Save ──────────────────────────────────────────────────

  const handleSave = async () => {
    if (!form.name.trim()) { notifications.show({ message: 'Template name is required', color: 'yellow' }); return; }
    if (steps.length === 0) { notifications.show({ message: 'Add at least one step', color: 'yellow' }); return; }

    setSaving(true);
    try {
      const triggerCfg = FRIENDLY_TRIGGER_CONFIG[form.trigger as FriendlyTrigger];
      const stepsData = JSON.stringify(steps);
      if (!stepsData || stepsData === '[]') {
        notifications.show({ message: 'Steps data is empty — cannot save', color: 'red' });
        setSaving(false);
        return;
      }
      // Resolve module for record_event_* templates from the chosen table
      let moduleForSave = form.module;
      if (form.trigger?.startsWith('record_event_') && form.triggerTable) {
        moduleForSave = getTableByKey(form.triggerTable)?.module || form.module;
      }

      const payload: Partial<WorkflowTemplate> = {
        name: form.name.trim(),
        description: form.description,
        module: moduleForSave,
        category: form.category,
        priority: form.priority,
        active: form.active ? 'yes' : 'no',
        triggerType: form.trigger,
        triggerTable: form.trigger === 'new_form_response'
          ? triggerFormId
          : form.trigger?.startsWith('record_event_')
            ? form.triggerTable
            : (triggerCfg?.table ?? ''),
        triggerField: form.trigger?.startsWith('record_event_') ? form.triggerField : '',
        triggerValue: form.trigger?.startsWith('record_event_') ? form.triggerValue : '',
        stepsJson: stepsData,
        creatorId: user?.email ?? '',
      };

      // Validation for record_event_* triggers
      if (form.trigger?.startsWith('record_event_') && !form.triggerTable) {
        notifications.show({ message: 'Please select a table for the record event trigger', color: 'yellow' });
        setSaving(false);
        return;
      }

      const stepCount = steps.length;
      if (editTarget) {
        await updateTemplate(editTarget.Rid, payload);
        notifications.show({ message: `Template updated (${stepCount} steps saved)`, color: 'green' });
      } else {
        await createTemplate(payload);
        notifications.show({ message: `Template created (${stepCount} steps saved)`, color: 'green' });
      }
      setEditorOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleSaveAndActivate = async () => {
    setForm(prev => ({ ...prev, active: true }));
    // Need a tick for state to settle, so just set active directly in the save
    const origActive = form.active;
    form.active = true;
    await handleSave();
    form.active = origActive;
  };

  // ── Delete ────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteTemplate(deleteTarget.Rid);
      notifications.show({ message: `"${deleteTarget.name}" deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  // ── Toggle active ─────────────────────────────────────────

  const toggleActive = async (t: WorkflowTemplate) => {
    const next = t.active === 'no' ? 'yes' : 'no';
    try {
      await updateTemplate(t.Rid, { active: next });
      notifications.show({ message: `${t.name} ${next === 'yes' ? 'activated' : 'deactivated'}`, color: next === 'yes' ? 'green' : 'gray' });
    } catch { /* ignore */ }
  };

  const fireNow = async (t: WorkflowTemplate) => {
    if (t.active === 'no') {
      notifications.show({ message: 'Activate the template before firing it', color: 'yellow' });
      return;
    }
    try {
      const startedBy = user?.fullName || user?.email || 'System';
      const sourceModule = t.module && t.module !== '*' ? t.module : (t.category || 'manual');
      const rid = await startWorkflow(
        String(t.Rid),
        sourceModule,
        '',
        '',
        `Manual fire: ${t.name}`,
        startedBy,
        { manual: true, firedAt: new Date().toISOString() },
      );
      if (rid) {
        notifications.show({ message: `Workflow "${t.name}" started (instance #${rid})`, color: 'green' });
      } else {
        notifications.show({ message: 'Failed to start workflow — check that it has steps defined', color: 'red' });
      }
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed to fire workflow', color: 'red' });
    }
  };

  // ── Render ────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Workflow Templates</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconSparkles size={14} />}
            onClick={() => setPresetOpen(true)}>
            Create from Preset
          </Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Create Custom
          </Button>
        </Group>
      </Group>

      {/* Search + Filter */}
      <Group mb="md" gap="sm">
        <TextInput size="xs" placeholder="Search templates..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.currentTarget.value)} style={{ flex: 1 }} />
        <Select size="xs" placeholder="All categories" clearable data={CATEGORY_OPTIONS}
          value={filterCategory} onChange={setFilterCategory} w={160} />
      </Group>

      {/* Template Cards */}
      {filtered.length === 0 && !loading ? (
        <Paper p="xl" radius="md" withBorder ta="center">
          <ThemeIcon size={48} radius="xl" variant="light" color="gray" mx="auto" mb="sm">
            <IconTemplate size={24} />
          </ThemeIcon>
          <Text c="dimmed" size="sm">
            {templates.length === 0 ? 'No workflow templates yet.' : 'No templates match your search.'}
          </Text>
          <Group justify="center" gap="xs" mt="md">
            <Button size="xs" variant="light" leftSection={<IconSparkles size={14} />}
              onClick={() => setPresetOpen(true)}>
              Start from Preset
            </Button>
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
              Create Custom
            </Button>
          </Group>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
          {filtered.map(t => {
            let stepCount = 0;
            try { stepCount = (JSON.parse(t.stepsJson) as WorkflowStepDef[]).length; } catch { /* ignore */ }
            const isActive = t.active !== 'no';
            const friendlyTrigger = triggerLabel(t.triggerType);

            return (
              <Card key={t.id} withBorder radius="sm" padding="sm" style={{ cursor: 'pointer' }}
                onClick={() => openEdit(t)}>
                <Group justify="space-between" mb={4} wrap="nowrap">
                  <Text size="sm" fw={600} lineClamp={1}>{t.name || '--'}</Text>
                  <Switch size="xs" checked={isActive}
                    onClick={e => { e.stopPropagation(); toggleActive(t); }} />
                </Group>

                <Text size="xs" c="dimmed" lineClamp={2} mb="xs">
                  {t.description || 'No description'}
                </Text>

                <Group gap={4} mb="xs">
                  <Badge size="xs" color={CATEGORY_COLORS[t.category] ?? 'gray'}>{t.category || 'custom'}</Badge>
                  <Badge size="xs" variant="outline">{moduleLabel(t.module)}</Badge>
                  <Badge size="xs" color={PRIORITY_COLORS_MANTINE[t.priority] ?? 'gray'} variant="dot">
                    {t.priority || 'normal'}
                  </Badge>
                </Group>

                <Group justify="space-between" mt="auto">
                  <Text size="xs" c="dimmed">
                    <IconPlayerPlay size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                    {friendlyTrigger}
                  </Text>
                  <Text size="xs" c="dimmed">{stepCount} step{stepCount !== 1 ? 's' : ''}</Text>
                </Group>

                <Divider my="xs" />

                <Group gap={4} justify="flex-end" onClick={e => e.stopPropagation()}>
                  <Tooltip label="Fire trigger now"><ActionIcon size="sm" variant="subtle" color="orange" onClick={() => fireNow(t)} disabled={!isActive}><IconBolt size={14} /></ActionIcon></Tooltip>
                  <Tooltip label="Edit"><ActionIcon size="sm" variant="subtle" onClick={() => openEdit(t)}><IconEdit size={14} /></ActionIcon></Tooltip>
                  <Tooltip label="Duplicate"><ActionIcon size="sm" variant="subtle" color="blue" onClick={() => openDuplicate(t)}><IconCopy size={14} /></ActionIcon></Tooltip>
                  <Tooltip label="Delete"><ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(t)}><IconTrash size={14} /></ActionIcon></Tooltip>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      )}

      {/* ── Preset Picker Modal ──────────────────────────────── */}
      <Modal opened={presetOpen} onClose={() => setPresetOpen(false)} title="Choose a Preset Workflow" size="lg">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {PRESET_WORKFLOWS.map((p, i) => (
            <Card key={i} withBorder radius="sm" padding="sm"
              style={{ cursor: 'pointer' }}
              onClick={() => openFromPreset(p)}>
              <Text size="sm" fw={600} mb={4}>{p.name}</Text>
              <Text size="xs" c="dimmed" lineClamp={2} mb="xs">{p.description}</Text>
              <Group gap={4}>
                <Badge size="xs" color={CATEGORY_COLORS[p.category]}>{p.category}</Badge>
                <Badge size="xs" variant="outline">{moduleLabel(p.module)}</Badge>
                <Text size="xs" c="dimmed" ml="auto">{p.steps.length} steps</Text>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      </Modal>

      {/* ── Template Editor Modal ─────────────────────────────── */}
      <Modal opened={editorOpen} onClose={() => setEditorOpen(false)}
        title={editTarget ? `Edit: ${editTarget.name}` : 'New Workflow Template'}
        size="xl"
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}>

        <Stack gap="sm">
          {/* Name + Description */}
          <TextInput label="Template Name" required size="sm" value={form.name}
            onChange={e => setField('name', e.currentTarget.value)}
            placeholder="e.g. Damage Assessment Review" />

          <Textarea label="Description" size="sm" autosize minRows={2} value={form.description}
            onChange={e => setField('description', e.currentTarget.value)}
            placeholder="When and why this workflow runs" />

          {/* Trigger */}
          <Select label="When does this start?" size="sm" data={TRIGGER_OPTIONS}
            value={form.trigger} searchable
            onChange={v => { setField('trigger', v ?? 'manual'); setTriggerFormId(''); }} />

          {/* Custom trigger — user defines the trigger name and description */}
          {form.trigger === 'custom_trigger' && (
            <Paper p="sm" withBorder style={{ borderColor: '#4B3FFF', background: '#F7F8FD' }}>
              <Text size="xs" fw={600} c="#4B3FFF" mb="xs">Define Your Custom Trigger</Text>
              <TextInput label="Trigger Title" size="sm" placeholder="e.g., Weather Alert Received, Supply Request, VIP Arrival"
                value={form.name.startsWith('[Custom] ') ? form.name.slice(9) : ''}
                onChange={e => setField('name', `[Custom] ${e.currentTarget.value}`)}
                mb="xs" required />
              <Textarea label="Trigger Description" size="sm" placeholder="Describe when this trigger fires and what data it provides"
                value={form.description}
                onChange={e => setField('description', e.currentTarget.value)}
                autosize minRows={2} mb="xs" />
              <Select label="Source Module" size="sm" placeholder="Which module triggers this?"
                data={[
                  { value: '', label: 'None (manual only)' },
                  { value: 'incidents', label: 'Incidents' },
                  { value: 'geoops', label: 'GeoOps' },
                  { value: 'mainstreet', label: 'MainStreet' },
                  { value: 'shelter', label: 'Shelter' },
                  { value: 'debris', label: 'Debris' },
                  { value: 'impact', label: 'Impact' },
                  { value: 'intake', label: 'Intake' },
                  { value: 'volunteer', label: 'VolunteerOS' },
                  { value: 'handoff', label: 'Handoff' },
                  { value: 'hotwash', label: 'HotWash' },
                  { value: 'pagrants', label: 'PA Grants' },
                ]}
                value={form.category || ''}
                onChange={v => setField('category', v ?? '')}
                clearable mb="xs" />
              <Select label="Priority" size="sm"
                data={[
                  { value: 'low', label: 'Low' },
                  { value: 'normal', label: 'Normal' },
                  { value: 'high', label: 'High' },
                  { value: 'critical', label: 'Critical' },
                ]}
                value={form.priority || 'normal'}
                onChange={v => setField('priority', v ?? 'normal')} mb="xs" />
              <Paper p="xs" withBorder style={{ background: '#fff' }}>
                <Text size="xs" fw={600} mb={4}>How Custom Triggers Work</Text>
                <Text size="xs" c="dimmed">
                  1. Define the trigger name and description above{'\n'}
                  2. Add workflow steps (approvals, tasks, notifications) below{'\n'}
                  3. Save the template — it appears in the Active Workflows page{'\n'}
                  4. Trigger it manually from the Inbox, or configure it to fire from module events{'\n'}
                  5. Each trigger run creates a workflow instance that tracks step completion
                </Text>
              </Paper>
            </Paper>
          )}

          {/* Trigger config — contextual UI based on trigger type */}
          {form.trigger === 'new_form_response' && (
            <Select
              label="Which form?"
              size="sm"
              placeholder="Select a published form..."
              data={formTemplateOpts}
              value={triggerFormId || null}
              onChange={v => setTriggerFormId(v ?? '')}
              searchable
              required
              description="This workflow will start automatically when a response is submitted to this form."
            />
          )}

          {/* Generic record_event_* picker — Module → Table → optional Field/Value */}
          {form.trigger?.startsWith('record_event_') && (() => {
            const eventLabel = form.trigger === 'record_event_create' ? 'created'
                             : form.trigger === 'record_event_update' ? 'updated'
                             : 'deleted';
            const tableOptions = form.triggerModule
              ? getTablesForModule(form.triggerModule).map(t => ({ value: t.tableKey, label: t.label }))
              : [];
            const fieldOptions = form.triggerTable
              ? getFieldNamesForTable(form.triggerTable).map(n => ({ value: n, label: fieldLabel(n) }))
              : [];
            return (
              <Paper p="sm" withBorder style={{ borderColor: '#4B3FFF', background: '#F7F8FD' }}>
                <Text size="xs" fw={600} c="#4B3FFF" mb="xs">
                  Trigger Configuration — fires when a record is {eventLabel}
                </Text>
                <SimpleGrid cols={2} spacing="xs" mb="xs">
                  <Select
                    label="Module"
                    size="sm"
                    placeholder="Choose a module..."
                    data={WORKFLOW_MODULE_OPTIONS}
                    value={form.triggerModule || null}
                    onChange={v => {
                      // Switching module clears the table + field selection
                      setForm(prev => ({
                        ...prev,
                        triggerModule: v ?? '',
                        triggerTable: '',
                        triggerField: '',
                        triggerValue: '',
                      }));
                    }}
                    searchable
                    required
                  />
                  <Select
                    label="Table"
                    size="sm"
                    placeholder={form.triggerModule ? 'Choose a table...' : 'Pick a module first'}
                    data={tableOptions}
                    value={form.triggerTable || null}
                    onChange={v => setForm(prev => ({
                      ...prev,
                      triggerTable: v ?? '',
                      triggerField: '',
                      triggerValue: '',
                    }))}
                    disabled={!form.triggerModule}
                    searchable
                    required
                  />
                </SimpleGrid>

                {form.triggerTable && (
                  <>
                    <Text size="xs" c="dimmed" mt="xs" mb={4}>
                      Optional — only fire when a specific field
                      {form.trigger === 'record_event_update' ? ' changes to' : ' equals'} a value:
                    </Text>
                    <SimpleGrid cols={2} spacing="xs">
                      <Select
                        label="Field (optional)"
                        size="sm"
                        placeholder="Any field"
                        data={[{ value: '', label: '— any field —' }, ...fieldOptions]}
                        value={form.triggerField || ''}
                        onChange={v => setField('triggerField', v ?? '')}
                        searchable
                        clearable
                      />
                      <TextInput
                        label="Value (optional)"
                        size="sm"
                        placeholder="Leave blank for any value"
                        value={form.triggerValue}
                        onChange={e => setField('triggerValue', e.currentTarget.value)}
                        disabled={!form.triggerField}
                      />
                    </SimpleGrid>

                    <Group justify="space-between" mt="sm">
                      <Text size="xs" c="dimmed">
                        Test against the most recent record in this table:
                      </Text>
                      <Button
                        size="xs" variant="light" color="orange"
                        leftSection={<IconBolt size={12} />}
                        onClick={async () => {
                          if (!editTarget) {
                            notifications.show({ message: 'Save the template first to test fire it', color: 'yellow' });
                            return;
                          }
                          try {
                            const { fetchTable } = await import('../../lib/api-switch');
                            const rows = await fetchTable(form.triggerTable, { limit: 5 });
                            if (rows.length === 0) {
                              notifications.show({ message: 'No records in this table to test against', color: 'yellow' });
                              return;
                            }
                            const sample = rows[0] as Record<string, unknown>;
                            const event = (form.trigger.replace('record_event_', '') as 'create' | 'update' | 'delete');
                            await useWorkflowStore.getState().fireRecordEvent(
                              form.triggerTable, event, String(sample.id || sample.Rid || '0'),
                              `TEST: ${form.name}`, sample, event === 'update' ? sample : undefined,
                            );
                            notifications.show({
                              message: `Test fired against record ${sample.id || sample.Rid}. Check Workflow History for the instance.`,
                              color: 'green', autoClose: 7000,
                            });
                          } catch (err) {
                            notifications.show({
                              message: err instanceof Error ? err.message : 'Test fire failed',
                              color: 'red',
                            });
                          }
                        }}
                      >
                        Test Fire
                      </Button>
                    </Group>
                  </>
                )}
              </Paper>
            );
          })()}
          {form.trigger !== 'manual' && form.trigger !== 'new_form_response' && (() => {
            const cfg = FRIENDLY_TRIGGER_CONFIG[form.trigger as FriendlyTrigger];
            return cfg ? (
              <Paper p="xs" radius="sm" withBorder style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Text size="xs" c="dimmed">
                  This workflow triggers automatically when {cfg.description}.
                </Text>
              </Paper>
            ) : null;
          })()}
          {form.trigger === 'manual' && (
            <Paper p="xs" radius="sm" withBorder style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
              <Text size="xs" c="dimmed">
                This workflow is started manually from the Inbox or from within a module.
              </Text>
            </Paper>
          )}

          {/* Category + Module + Priority */}
          <Group grow gap="sm">
            <Select label="Category" size="sm" data={CATEGORY_OPTIONS}
              value={form.category} onChange={v => setField('category', v ?? 'approval')} />
            <Select label="Module" size="sm" data={MODULE_OPTIONS}
              value={form.module} onChange={v => setField('module', v ?? '*')} />
            <Select label="Priority" size="sm" data={PRIORITY_OPTIONS}
              value={form.priority} onChange={v => setField('priority', v ?? 'normal')} />
          </Group>

          {/* Steps */}
          <Divider label={<Group gap={6}><Text size="sm" fw={600}>Steps</Text><Badge size="xs" circle>{steps.length}</Badge></Group>} labelPosition="left" />

          <Stack gap="xs">
            {steps.map((step, i) => (
              <StepRow key={i} step={step} index={i} total={steps.length}
                onChange={s => updateStep(i, s)}
                onMoveUp={() => moveUp(i)} onMoveDown={() => moveDown(i)}
                onDelete={() => removeStep(i)}
                roleOptions={roleOpts} userOptions={userOpts} formFieldOptions={formFieldOptions}
                allStepNames={steps.map(s => s.name)} formFieldMeta={formFieldMeta} />
            ))}
          </Stack>

          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addStep}
            style={{ alignSelf: 'flex-start' }}>
            Add Step
          </Button>

          <Divider />

          {/* Actions */}
          <Group justify="space-between">
            <Switch label="Active" checked={form.active}
              onChange={e => setField('active', e.currentTarget.checked)} />
            <Group gap="sm">
              <Button variant="default" size="sm" onClick={() => setEditorOpen(false)}>Cancel</Button>
              {!form.active && (
                <Button size="sm" variant="light" loading={saving} onClick={handleSaveAndActivate}>
                  Save and Activate
                </Button>
              )}
              <Button size="sm" loading={saving} onClick={handleSave}>
                {editTarget ? 'Save Changes' : 'Create Template'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* ── Delete Confirmation ────────────────────────────────── */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        title="Delete Template" centered size="sm">
        <Stack>
          <Text size="sm">
            Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone. Active instances
            from this template will not be affected.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
